import React, { useState } from 'react';
import {
    Card,
    CardHeader,
    CardBody,
    Heading,
    Page,
    PageContent,
    Box,
    Form,
    FormField,
    TextInput,
    Button,
    TextArea,
    FileInput,
    Notification,
    CheckBox,
} from 'grommet';
import { StatusCritical, StatusGood } from 'grommet-icons';
import { FilterOptionSelect, API } from '@enrich/shared-lib';
import { useLoaderData, useNavigate } from 'react-router-dom';

const filterFieldsQuery = async () => {
    const query = `query {
        getFilterFields {
          locations {
            id
            name
          }
          industries {
            id
            name
          }
        }
      }`;

    try {
        const graphqlResponse = await API.post('graphql', { query });
        const res = graphqlResponse.data.data.getFilterFields;
        return res;
    } catch (error) {
        console.log(error);
        return [];
    }
};

export async function loader() {
    let filterFields;
    try {
        filterFields = await filterFieldsQuery();
    } catch (error) {
        console.log(error);
        return [];
    }

    return filterFields;
}

export const NewCompanyForm = () => {
    const navigate = useNavigate();
    const loaderData = useLoaderData() || {};
    const [industries, setIndustries] = useState([]);
    const [locations, setLocations] = useState([]);
    const [showSuccessNotification, setShowSuccessNotification] =
        useState(false);
    const [failureNotification, setFailureNotification] = useState({
        isVisible: false,
        message: '',
    });

    const initialValue = {
        name: '',
        isContractClient: false,
        description: '',
        size: '',
        history: '',
        website: '',
        appliedStageReminderDays: 0,
        location: '',
    };

    const [value, setValue] = useState(initialValue);
    const [buttonBusy, setButtonBusy] = useState(false);

    const handleReset = () => {
        setValue(initialValue);
    };

    const handleSubmit = async (newCompanyData) => {
        setButtonBusy(true);
        const locationIds = locations.map((l) => l.id);
        const industryIds = industries.map((i) => i.id);

        let request = {
            ...newCompanyData,
            locations: locationIds,
            industries: industryIds,
        };

        let response;
        if (newCompanyData.logo) {
            try {
                response = await API.postForm(
                    '/api/upload',
                    { files: newCompanyData.logo[0] },
                    {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    },
                );
            } catch (error) {
                console.log(error?.message);
                setFailureNotification({
                    isVisible: true,
                    message:
                        'Failed to upload company logo. Please try again or contact support.',
                });
                setButtonBusy(false);
                return;
            }
        }

        request.logo =
            response?.data && response.data.length > 0
                ? { id: response.data[0].id }
                : undefined;

        try {
            response = await API.post('/api/companies', {
                data: request,
            });
            const newCompanyId = response.data.data.id;
            navigate(`/companies/${newCompanyId}`);
        } catch (error) {
            setFailureNotification({ isVisible: true });
            setButtonBusy(false);
            return;
        }

        setShowSuccessNotification(true);
        handleReset();
    };

    return (
        <>
            {showSuccessNotification && (
                <Notification
                    toast={{ autoClose: true, position: 'top' }}
                    status="info"
                    time={8000}
                    title="Company created successfully!"
                    icon={<StatusGood color="green" />}
                    onClose={() => setShowSuccessNotification(false)}
                />
            )}

            {failureNotification.isVisible && (
                <Notification
                    toast={{ autoClose: true, position: 'top' }}
                    status="info"
                    time={8000}
                    title="Failed to create company!"
                    message={
                        failureNotification.message ||
                        'Please try again or contact support.'
                    }
                    icon={<StatusCritical color="red" />}
                    onClose={() =>
                        setFailureNotification({
                            isVisible: false,
                            message: '',
                        })
                    }
                />
            )}

            <Page kind="narrow">
                <PageContent margin={{ vertical: '30px' }}>
                    <Card width="large" background="light-1">
                        <CardHeader pad="medium" justify="center">
                            <Heading level="2">New Company</Heading>
                        </CardHeader>
                        <CardBody pad="medium" overflow={{ vertical: 'auto' }}>
                            <Form
                                value={value}
                                onChange={(nextValue) => setValue(nextValue)}
                                onReset={handleReset}
                                onSubmit={({ value }) => {
                                    handleSubmit(value);
                                }}>
                                <FormField
                                    name="name"
                                    htmlFor="name"
                                    label="Name">
                                    <TextInput id="name" name="name" required />
                                </FormField>

                                <FormField
                                    name="isContractClient"
                                    htmlFor="isContractClient"
                                    label="Is Contract Client">
                                    <CheckBox
                                        defaultChecked={false}
                                        checked={value.isContractClient}
                                        onChange={(event) =>
                                            setValue({
                                                ...value,
                                                isContractClient:
                                                    event.target.checked,
                                            })
                                        }
                                    />
                                </FormField>

                                <Box direction="row">
                                    <Box
                                        margin={{ end: 'large' }}
                                        fill="horizontal">
                                        <FormField
                                            name="appliedStageReminderDays"
                                            htmlFor="appliedStageReminderDays"
                                            label="Applied Stage Reminder in Days">
                                            <TextInput
                                                type="number"
                                                min={0}
                                                id="appliedStageReminderDays"
                                                name="appliedStageReminderDays"
                                                required
                                            />
                                        </FormField>
                                    </Box>
                                    <Box fill="horizontal">
                                        <FormField
                                            name="size"
                                            htmlFor="size"
                                            label="Size">
                                            <TextInput
                                                id="size"
                                                name="size"
                                                required
                                            />
                                        </FormField>
                                    </Box>
                                </Box>

                                <FormField
                                    name="website"
                                    htmlFor="website"
                                    label="Website Url">
                                    <TextInput
                                        type="url"
                                        id="website"
                                        name="website"
                                    />
                                </FormField>

                                <FormField
                                    name="logo"
                                    htmlFor="logo"
                                    label="Company Logo">
                                    <FileInput id="logo" name="logo" />
                                </FormField>

                                <FormField
                                    name="industries"
                                    htmlFor="industries"
                                    label="Industries">
                                    <FilterOptionSelect
                                        labelKey="name"
                                        placeholder="select industries the company operates"
                                        options={loaderData.industries}
                                        updateFormData={setIndustries}
                                        valueKey="id"
                                    />
                                </FormField>

                                <FormField
                                    name="locations"
                                    htmlFor="locations"
                                    label="Locations">
                                    <FilterOptionSelect
                                        labelKey="name"
                                        placeholder="select company locations"
                                        options={loaderData.locations}
                                        updateFormData={setLocations}
                                        valueKey="id"
                                    />
                                </FormField>

                                <FormField
                                    name="locationDescription"
                                    htmlFor="locationDescription"
                                    label="Location Description">
                                    <TextArea
                                        id="locationDescription"
                                        name="location"
                                    />
                                </FormField>

                                <FormField
                                    name="description"
                                    htmlFor="description"
                                    label="Company Description">
                                    <TextArea
                                        id="description"
                                        name="description"
                                        required
                                    />
                                </FormField>

                                <FormField
                                    name="history"
                                    htmlFor="history"
                                    label="Company History">
                                    <TextArea
                                        id="history"
                                        name="history"
                                        required
                                    />
                                </FormField>

                                <Box
                                    direction="row"
                                    margin={{ top: '30px' }}
                                    gap="large"
                                    justify="center">
                                    <Button
                                        busy={buttonBusy}
                                        size="large"
                                        type="submit"
                                        primary
                                        label="Submit"
                                    />
                                    <Button
                                        size="large"
                                        type="reset"
                                        label="Reset"
                                    />
                                </Box>
                            </Form>
                        </CardBody>
                    </Card>
                </PageContent>
            </Page>
        </>
    );
};
